import { AxiosResponse } from 'axios';

import { GetPartnersPayload } from '@/api/partners/request';
import { ClientService } from '@/types/api';
import {
	SelectionListResponse,
	SelectionFieldsResponse,
	ResponseSelections,
	TutorialAdminItem,
	TutorialItem,
	TemplateFieldsDto,
	TutorialHistoryItem,
} from './response';
import { TutorialFormOutputDTO } from '@/features/tutorial/dtos/tutorial';
import { TutorialSectionInSelection, TutorialSectionOfficial } from '@workspace/shared-constants';

export class TutorialApi {
	private prefix = 'tutorial';

	constructor(private readonly clientService: ClientService) {
		this.clientService = clientService;
	}

	public async getList({ filters, ...params }: GetPartnersPayload): Promise<AxiosResponse<SelectionListResponse>> {
		return this.clientService.post(`${this.prefix}/list`, filters, {
			params,
		});
	}

	public async get(
		section: TutorialSectionInSelection | TutorialSectionOfficial,
		selectionId: number | undefined
	): Promise<AxiosResponse<TutorialItem | null>> {
		return this.clientService.get(`${this.prefix}/${section}`, {
			params: {
				selectionId,
			},
		});
	}

	public async getTemplateFields(selectionId: number): Promise<AxiosResponse<TemplateFieldsDto>> {
		return this.clientService.get(`${this.prefix}/fields/${selectionId}`);
	}

	public async getAdminTutorial(tutorialId: number): Promise<AxiosResponse<TutorialAdminItem>> {
		return this.clientService.get(`${this.prefix}/admin/${tutorialId}`);
	}

	public async create(payload: TutorialFormOutputDTO): Promise<AxiosResponse<void>> {
		return this.clientService.post(this.prefix, payload);
	}

	public async update(tutorialId: number, payload: TutorialFormOutputDTO): Promise<AxiosResponse<void>> {
		return this.clientService.put(`${this.prefix}/${tutorialId}`, payload);
	}

	public async delete(tutorialId: number): Promise<AxiosResponse<void>> {
		return this.clientService.delete(`${this.prefix}/${tutorialId}`);
	}

	public async download(tutorialId: number): Promise<AxiosResponse<Blob>> {
		return this.clientService.get(`${this.prefix}/download/${tutorialId}`, {
			responseType: 'blob',
		});
	}

	public async downloadArchive(selectionId: number | null): Promise<AxiosResponse<Blob>> {
		return this.clientService.get(`${this.prefix}/download-archive/${selectionId || 0}`, {
			responseType: 'blob',
		});
	}

	public async downloadSelected(tutorialIds: number[]): Promise<AxiosResponse<Blob>> {
		return this.clientService.post(
			`${this.prefix}/download-selected/`,
			{ tutorialIds },
			{
				responseType: 'blob',
			}
		);
	}

	public async getSelectionOptions(
		search: string,
		update: boolean = false
	): Promise<AxiosResponse<ResponseSelections>> {
		return this.clientService.get(`${this.prefix}/selection-options`, {
			params: {
				search,
				update,
			},
		});
	}

	public async getSelectionFieldsOptions(selectionId: number): Promise<AxiosResponse<SelectionFieldsResponse>> {
		return this.clientService.get(`${this.prefix}/visible-fields-options/${selectionId}`);
	}

	public async getHistory(tutorialId: number): Promise<AxiosResponse<TutorialHistoryItem[]>> {
		return this.clientService.get(`${this.prefix}/history/${tutorialId}`);
	}

	public async versionRevert(tutorialHistoryId: number): Promise<AxiosResponse<TutorialHistoryItem[]>> {
		return this.clientService.put(`${this.prefix}/version-revert/${tutorialHistoryId}`);
	}
}
